import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Info,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import type { CheckStatus, DCMACheck, DCMAResult } from '../../services/dcmaAnalyzer';

interface DcmaScorecardProps {
  result: DCMAResult;
  onDrillThrough: (activityIds: string[], label: string) => void;
}

const STATUS_META: Record<
  CheckStatus,
  { chip: string; colour: string; icon: typeof CheckCircle2; label: string }
> = {
  pass: { chip: 'chip-pass', colour: 'var(--status-pass)', icon: CheckCircle2, label: 'Pass' },
  warning: { chip: 'chip-warn', colour: 'var(--status-warn)', icon: AlertTriangle, label: 'Watch' },
  fail: { chip: 'chip-fail', colour: 'var(--status-fail)', icon: XCircle, label: 'Fail' },
  info: { chip: 'chip-info', colour: 'var(--mr-vivid-blue)', icon: Info, label: 'Info' },
  na: { chip: 'chip-na', colour: 'var(--text-muted)', icon: MinusCircle, label: 'N/A' },
};

/**
 * The 14 checks as a scannable grid rather than an accordion list.
 *
 * Every check with failures is a route into the activities that caused it —
 * "47 activities missing logic" is only useful if you can then see which 47.
 */
export function DcmaScorecard({ result, onDrillThrough }: DcmaScorecardProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const counts = result.checks.reduce<Record<CheckStatus, number>>(
    (acc, check) => ({ ...acc, [check.status]: (acc[check.status] ?? 0) + 1 }),
    { pass: 0, warning: 0, fail: 0, info: 0, na: 0 }
  );

  return (
    <div className="panel panel-accent">
      <div className="panel-header flex-wrap">
        <div>
          <h3 className="panel-title">DCMA 14-Point Assessment</h3>
          <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            {result.incompleteCount} incomplete activities assessed
            {result.dataDate &&
              ` against a data date of ${result.dataDate.toLocaleDateString('en-GB')}`}
            {!result.hasBaseline && ' · no baseline loaded, 2 checks unavailable'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="chip chip-pass">{counts.pass} pass</span>
          {counts.warning > 0 && <span className="chip chip-warn">{counts.warning} watch</span>}
          {counts.fail > 0 && <span className="chip chip-fail">{counts.fail} fail</span>}
          {counts.na > 0 && <span className="chip chip-na">{counts.na} n/a</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
        {result.checks.map((check) => (
          <CheckCell
            key={check.key}
            check={check}
            isExpanded={expanded === check.key}
            onToggle={() => setExpanded(expanded === check.key ? null : check.key)}
            onDrillThrough={onDrillThrough}
          />
        ))}
      </div>
    </div>
  );
}

function CheckCell({
  check,
  isExpanded,
  onToggle,
  onDrillThrough,
}: {
  check: DCMACheck;
  isExpanded: boolean;
  onToggle: () => void;
  onDrillThrough: (activityIds: string[], label: string) => void;
}) {
  const meta = STATUS_META[check.status];
  const Icon = meta.icon;
  const canDrill = check.failingActivityIds.length > 0;

  // Proportional checks get a bar against their 5% threshold so the reader can
  // see how far past the line it is, not just that it crossed.
  const showBar = check.ratio !== null && check.status !== 'na';
  const barRatio = Math.min(1, (check.ratio ?? 0) / 0.2);
  const thresholdMark = 0.05 / 0.2;

  return (
    <div
      className="border-b border-r p-4 transition-colors"
      style={{ borderColor: 'var(--border-subtle)' }}
    >
      <button
        className="flex w-full items-start gap-3 text-left"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: meta.colour }} />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className="tnum text-[0.62rem] font-medium"
              style={{ color: 'var(--text-muted)' }}
            >
              {String(check.number).padStart(2, '0')}
            </span>
            <span
              className="truncate text-sm font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              {check.name}
            </span>
          </div>

          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="tnum text-lg font-bold" style={{ color: meta.colour }}>
              {check.value}
            </span>
            <span className="text-[0.68rem]" style={{ color: 'var(--text-muted)' }}>
              target {check.threshold}
            </span>
          </div>

          {showBar && (
            <div
              className="relative mt-2 h-1 w-full overflow-hidden rounded-sm"
              style={{ background: 'var(--surface-inset)' }}
            >
              <div
                className="h-full rounded-sm transition-[width] duration-700"
                style={{ width: `${barRatio * 100}%`, background: meta.colour }}
              />
              <div
                className="absolute top-0 h-full w-px"
                style={{ left: `${thresholdMark * 100}%`, background: 'var(--text-muted)' }}
              />
            </div>
          )}
        </div>

        <ChevronRight
          className="mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform"
          style={{
            color: 'var(--text-muted)',
            transform: isExpanded ? 'rotate(90deg)' : undefined,
          }}
        />
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2 pl-7">
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {check.description}
          </p>
          <p className="text-xs italic leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {check.guidance}
          </p>
          {canDrill && (
            <button
              className="icon-button"
              onClick={() =>
                onDrillThrough(check.failingActivityIds, `DCMA ${check.number}: ${check.name}`)
              }
            >
              Show {check.failingActivityIds.length} activities
              <ChevronRight className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
